import toast from 'react-hot-toast';
import { NavigateFunction } from "react-router-dom";

export interface UserSession {
  token: string;
  userName: string;
  userEmail: string;
  userId: string;
  tenantId: string;
  role: string;
}

// Function to get current user session
export const getCurrentSession = (): UserSession | null => {
  const token = localStorage.getItem('authToken');
  const userName = localStorage.getItem('userName');
  const userEmail = localStorage.getItem('userEmail');
  const userId = localStorage.getItem('userId');
  const tenantId = localStorage.getItem('tenantId');
  const role = localStorage.getItem('userRole');

  if (!token || !userName || !userEmail || !userId || !tenantId || !role) {
    return null;
  }

  return {
    token,
    userName,
    userEmail,
    userId,
    tenantId,
    role
  };
};

// Function to check if user is authenticated
export const isAuthenticated = (): boolean => {
  const session = getCurrentSession();
  if (!session) return false;
  
  return !isTokenExpired(session.token);
};

// Function to check if token is expired
export const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    return payload.exp < currentTime;
  } catch (error) {
    return true; // If we can't parse the token, consider it expired
  }
};

// Function to clear all user data and sign out
export const clearUserSession = (navigate?: NavigateFunction) => {
  // Clear all localStorage items
  localStorage.removeItem('authToken');
  localStorage.removeItem('userName');
  localStorage.removeItem('userEmail');
  localStorage.removeItem('userId');
  localStorage.removeItem('tenantId');
  
  // Show notification
  toast.error('Your session has expired. Please sign in again.', {
    duration: 4000,
    position: 'top-right',
  });
  
  // Navigate to signin page if navigate function is provided
  if (navigate) {
    navigate("/signin");
  } else {
    // If no navigate function, redirect using window.location
    window.location.href = '/signin';
  }
};

// Function to set user session
export const setUserSession = (session: UserSession) => {
  localStorage.setItem('authToken', session.token);
  localStorage.setItem('userName', session.userName);
  localStorage.setItem('userEmail', session.userEmail);
  localStorage.setItem('userId', session.userId);
  localStorage.setItem('tenantId', session.tenantId);
  localStorage.setItem('userRole', session.role);
};

// Function to get token with expiration check
export const getValidToken = (): string | null => {
  const token = localStorage.getItem('authToken');
  if (!token) return null;
  
  if (isTokenExpired(token)) {
    clearUserSession();
    return null;
  }
  
  return token;
};

// Function to check session status and handle expiration
export const checkSessionStatus = (navigate?: NavigateFunction): boolean => {
  const session = getCurrentSession();
  
  if (!session) {
    clearUserSession(navigate);
    return false;
  }
  
  if (isTokenExpired(session.token)) {
    clearUserSession(navigate);
    return false;
  }
  
  return true;
};

// Function to get remaining time until token expires (in minutes)
export const getTokenExpirationTime = (): number | null => {
  const token = localStorage.getItem('authToken');
  if (!token) return null;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    const remainingTime = payload.exp - currentTime;
    return Math.max(0, Math.floor(remainingTime / 60)); // Return minutes
  } catch (error) {
    return null;
  }
};

// Function to check if token will expire soon (within 5 minutes)
export const isTokenExpiringSoon = (): boolean => {
  const remainingTime = getTokenExpirationTime();
  return remainingTime !== null && remainingTime <= 5;
}; 